import { captureException } from '@sentry/vue';
import type { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';

// Function to get the API instance
const getApi = (): AxiosInstance => {
  const nuxtApp = useNuxtApp();
  return nuxtApp.$api as AxiosInstance;
};

// Error handling function
const handleError = (error: any): any => {
  console.error('API request failed:', error);
  captureException(error);
  return error;
};

// GET request function
export const get = async <T>(endpoint: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  try {
    const api = getApi();
    const response: AxiosResponse<T> = await api.get(endpoint, { ...config, withCredentials: true });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      config: response.config
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAttachment = async (
  table: string,
  identifier: string,
  fileName: string
): Promise<AxiosResponse<ArrayBuffer>> => {
  const response = await get<ArrayBuffer>(`v1/attachments/${table}-${identifier}-${fileName}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/pdf'
    }
  });

  return response; // Return the full Axios response
};

export const getSpreadsheetAttachment = async (
  table: string,
  identifier: string,
  fileName: string
): Promise<AxiosResponse<ArrayBuffer>> => {
  const response = await get<ArrayBuffer>(`v1/view-spreadsheet/${table}-${identifier}-${fileName}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json'
    }
  });
  return response;
};

export const getReceipt = async (identifier: string): Promise<AxiosResponse<ArrayBuffer>> => {
  const response = await get<ArrayBuffer>(`v1/donation_receipts/${identifier}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/pdf'
    }
  });

  return response; // Return the full Axios response
};
// POST request function
export const post = async <T>(endpoint: string, data?: any, params?: any): Promise<AxiosResponse<T>> => {
  try {
    const api = getApi();
    const response: AxiosResponse<T> = await api.post(endpoint, data, {
      withCredentials: true,
      params
    });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      config: response.config
    };
  } catch (error) {
    return handleError(error);
  }
};

export const postExport = async (
  endpoint: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<ArrayBuffer>> => {
  try {
    const api = getApi();
    const response = await api.post(endpoint, config, {
      withCredentials: true,
      responseType: 'arraybuffer',
      headers: {
        Accept: '*/*'
      }
    });
    return response;
  } catch (error) {
    return handleError(error);
  }
};

export const parseArrayBufferError = (response: any) => {
  try {
    const decoded = new TextDecoder('utf-8').decode(response.data);
    const parsed = JSON.parse(decoded);
    return parsed;
  } catch {
    return null;
  }
};

// export const postExport = async (endpoint: string, file_format: string): Promise<AxiosResponse<ArrayBuffer>> => {
//   const response = await post<ArrayBuffer>(endpoint, {
//     data: { file_format },
//     responseType: 'arraybuffer',
//     headers: {
//       Accept: '*/*'
//     }
//   });
//   return response; // Return the full Axios response
// };

export const postForm = async <T>(endpoint: string, formData: any): Promise<AxiosResponse<T>> => {
  try {
    const api = getApi();
    const response: AxiosResponse<T> = await api.postForm(endpoint, formData, { withCredentials: true });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      config: response.config
    };
  } catch (error) {
    return handleError(error);
  }
};

export const postFormForDownload = async (endpoint: string, formData: any): Promise<AxiosResponse<Blob>> => {
  const api = getApi();

  return api.postForm(endpoint, formData, {
    withCredentials: true,
    responseType: 'blob' // 🔥 critical
  });
};

export const put = async <T>(endpoint: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  try {
    const api = getApi();
    const response: AxiosResponse<T> = await api.put(endpoint, config, { withCredentials: true }); // Pass FormData directly
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      config: response.config
    };
  } catch (error) {
    return handleError(error);
  }
};

export const putForm = async <T>(endpoint: string, formData: any): Promise<AxiosResponse<T>> => {
  try {
    const api = getApi();
    const response: AxiosResponse<T> = await api.putForm(endpoint, formData, { withCredentials: true }); // Pass FormData directly
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      config: response.config
    };
  } catch (error) {
    return handleError(error);
  }
};

export const del = async <T>(endpoint: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  try {
    const api = getApi();
    const response: AxiosResponse<T> = await api.delete(endpoint, { ...config, withCredentials: true });
    return {
      headers: response.headers,
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      config: response.config
    };
  } catch (error) {
    return handleError(error);
  }
};
